import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  Plus,
  RefreshCw,
  Search,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";

import { AddMaquilaClientDialog } from "./components/AddMaquilaClientDialog";
import { ImportClientDialog } from "./components/ImportClientDialog";

interface MaquilaClient {
  id: string;
  name: string | null;
  tax_id: string | null;
  phone_number: string | null;
  email: string | null;
  contact_person: string | null;
  created_at: string;
}

export const MaquilaClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<MaquilaClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchClients = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from("clients")
      .select(
        "id, name, tax_id, phone_number, email, contact_person, created_at"
      )
      .eq("is_maquila_client", true)
      .order("name");
    if (data) setClients(data);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleClientAdded = () => {
    fetchClients();
  };

  const handleRemoveClient = async (client: MaquilaClient) => {
    setRemovingId(client.id);
    try {
      const { error } = await supabase
        .from("clients")
        .update({ is_maquila_client: false })
        .eq("id", client.id);

      if (error) throw error;

      toast({
        title: "Cliente removido",
        description: `"${client.name}" ya no es cliente maquila.`,
      });
      fetchClients();
    } catch (error) {
      console.error("Error removing client:", error);
      toast({
        title: "Error",
        description: "No se pudo remover el cliente. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setRemovingId(null);
    }
  };

  // Filter clients by search query
  const filteredClients = clients.filter((client) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.tax_id?.toLowerCase().includes(query) ||
      client.contact_person?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="mx-auto w-full p-6 lg:w-[80vw]">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/maquila")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Clientes Maquila
            </h1>
            <p className="text-sm text-muted-foreground">
              Gestionar clientes del servicio de maquila
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => fetchClients()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Importar Cliente
          </Button>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nombre, RUT, contacto o email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : clients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="mb-2 h-8 w-8" />
              <p>No hay clientes maquila registrados</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => setDialogOpen(true)}
              >
                Crear primer cliente
              </Button>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Search className="mb-2 h-8 w-8" />
              <p>No se encontraron clientes con "{searchQuery}"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre / Razon Social</TableHead>
                  <TableHead>RUT</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Telefono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client, index) => (
                  <TableRow
                    key={client.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                  >
                    <TableCell className="font-medium">
                      {client.name || "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {client.tax_id || "-"}
                    </TableCell>
                    <TableCell>{client.contact_person || "-"}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {client.phone_number || "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {client.email || "-"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => handleRemoveClient(client)}
                        disabled={removingId === client.id}
                        title="Remover de maquila"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <AddMaquilaClientDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onClientAdded={handleClientAdded}
      />

      {/* Import Client Dialog */}
      <ImportClientDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onClientImported={fetchClients}
      />
    </div>
  );
};
